import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import type { z } from "zod";

import axios from "axios";
import FormData from "form-data";

import { getCsrfToken } from "@/providers/auth";
import { handleSessionExpired } from "@/lib/session-expiration";
import type { APIConfigProps } from "./api-config";
import type { ApiRequestOptions } from "./api-request-options";
import type { ApiResult } from "./api-result";
import type { OnCancel } from "./cancelable-promise";

import { ApiError } from "./api-error";
import { CancelablePromise } from "./cancelable-promise";

export const isDefined = <T>(
  value: null | T | undefined,
): value is Exclude<T, null | undefined> => {
  return value !== undefined && value !== null;
};

export const isString = (value: unknown): value is string => {
  return typeof value === "string";
};

export const isStringWithValue = (value: unknown): value is string => {
  return isString(value) && value !== "";
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const isBlob = (value: any): value is Blob => {
  return (
    typeof value === "object" &&
    typeof value.type === "string" &&
    typeof value.stream === "function" &&
    typeof value.arrayBuffer === "function" &&
    typeof value.constructor === "function" &&
    typeof value.constructor.name === "string" &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

export const isFormData = (value: unknown): value is FormData => {
  return value instanceof FormData;
};

export const isSuccess = (status: number): boolean => {
  return status >= 200 && status < 300;
};

export const base64 = (str: string): string => {
  try {
    return btoa(str);
  } catch (_) {
    return Buffer.from(str).toString("base64");
  }
};

export const getQueryString = (params: Record<string, unknown>): string => {
  const qs: string[] = [];

  const append = (key: string, value: unknown) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  // biome-ignore lint/suspicious/noExplicitAny: needed for processing complex nested objects
  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          process(key, v);
        });
      } else if (typeof value === "object") {
        Object.entries(value).forEach(([k, v]) => {
          process(`${key}[${k}]`, v);
        });
      } else {
        append(key, value);
      }
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  if (qs.length > 0) {
    return `?${qs.join("&")}`;
  }

  return "";
};

const getUrl = <T extends string>(
  config: APIConfigProps,
  options: ApiRequestOptions<T>,
): string => {
  const encoder = config.ENCODE_PATH ?? encodeURI;

  const path = options.url
    .replace("{api-version}", config.VERSION)
    .replace(/{(.*?)}/g, (substring: string, group: string) => {
      // biome-ignore lint/suspicious/noPrototypeBuiltins: needed for property check
      if (options.path?.hasOwnProperty(group)) {
        // @ts-expect-error - types
        return encoder(String(options.path[group]));
      }
      return substring;
    });

  const url = `${config.BASE}${path}`;
  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

export const getFormData = <T extends string>(
  options: ApiRequestOptions<T>,
): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: unknown) => {
      if (isString(value) || isBlob(value)) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    };

    Object.entries(options.formData)

      .filter(([_, value]) => isDefined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => process(key, v));
        } else {
          process(key, value);
        }
      });

    return formData;
  }
  return undefined;
};

type Resolver<T, U extends string> = (
  options: ApiRequestOptions<U>,
) => Promise<T>;

export const resolve = async <T, U extends string>(
  options: ApiRequestOptions<U>,
  resolver?: Resolver<T, U> | T,
): Promise<T | undefined> => {
  if (typeof resolver === "function") {
    return await (resolver as Resolver<T, U>)(options);
  }
  return resolver;
};

export const getHeaders = async <T extends string>(
  config: APIConfigProps,
  options: ApiRequestOptions<T>,
  formData?: FormData,
): Promise<Record<string, string>> => {
  const additionalHeaders = await resolve(options, config.HEADERS);
  const formHeaders =
    (typeof formData?.getHeaders === "function" && formData?.getHeaders()) ||
    {};
  const csrfToken = getCsrfToken();

  const headers = Object.entries({
    Accept: "application/json",
    ...additionalHeaders,
    ...options.headers,
    ...formHeaders,
  })

    .filter(([_, value]) => isDefined(value))
    .reduce<Record<string, string>>(
      (headers, [key, value]) => ({
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        ...headers,
        [key]: String(value),
      }),
      {},
    );

  if (csrfToken) {
    headers["X-CSRFToken"] = csrfToken;
  }

  if (options.body) {
    if (options.mediaType) {
      headers["Content-Type"] = options.mediaType;
    } else if (isBlob(options.body)) {
      headers["Content-Type"] = options.body.type || "application/octet-stream";
    } else if (isString(options.body)) {
      headers["Content-Type"] = "text/plain";
    } else if (!isFormData(options.body)) {
      headers["Content-Type"] = "application/json";
    }
  }

  return headers;
};

export const getRequestBody = <T extends string>(
  options: ApiRequestOptions<T>,
): unknown => {
  if (options.body) {
    return options.body;
  }
  return undefined;
};

const logDev = (key: string, value: unknown) => {
  if (typeof window !== "undefined") {
    console.info(`\t- ${key}:`, value);
  } else {
    console.info(
      `\t- ${key}: ${JSON.stringify(typeof value === "undefined" ? null : value).slice(0, 100)}...`,
    );
  }
};

export const sendRequest = async <T, U extends string>(
  config: APIConfigProps,
  options: ApiRequestOptions<U>,
  url: string,
  body: unknown,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel,
  axiosClient: AxiosInstance,
): Promise<AxiosResponse<T>> => {
  const source = axios.CancelToken.source();

  const requestConfig: AxiosRequestConfig = {
    cancelToken: source.token,
    data: body ?? formData,
    headers,
    method: options.method,
    url,
    withCredentials: config.WITH_CREDENTIALS,
  };

  onCancel(() => source.cancel("The user aborted a request."));

  try {
    const response = await axiosClient.request(requestConfig);

    if (process.env.NODE_ENV === "development") {
      console.info("====DEV=CONSOLE====");
      console.info(`  > ${options.method}: ${url}`);
      console.info(`\t- ${response.status}: ${response.statusText}`);
      logDev("BODY", body);
      logDev("RESPONSE", response.data);
      console.info();
    }

    return response;
  } catch (error) {
    const axiosError = error as AxiosError<T>;

    if (process.env.NODE_ENV === "development") {
      console.info("====DEV=CONSOLE====");
      console.info(`  > ${options.method}: ${url}`);
      console.info(`\t- ERROR: ${axiosError.message}`);
      console.info();
    }

    if (axiosError.response) {
      return axiosError.response;
    }
    throw error;
  }
};

export const getResponseHeader = (
  response: AxiosResponse<unknown>,
  responseHeader?: string,
): string | undefined => {
  if (responseHeader) {
    const content = response.headers[responseHeader];
    if (isString(content)) {
      return content;
    }
  }
  return undefined;
};

export const getResponseBody = async (
  response: AxiosResponse<unknown>,
  schema?: z.ZodSchema,
) => {
  if (response.status !== 204 && response.status < 400) {
    return schema ? await schema.parseAsync(response.data) : response.data;
  }
  return response.data;
};

export const catchErrorCodes = <T extends string>(
  options: ApiRequestOptions<T>,
  result: ApiResult,
): void => {
  const errors: Record<number, string> = {
    400: "Bad Request",
    401: "Unauthorized",
    403: "Forbidden",
    404: "Not Found",
    500: "Internal Server Error",
    502: "Bad Gateway",
    503: "Service Unavailable",
    ...options.errors,
  };

  const error = errors[result.status];
  if (error) {
    if (result.status === 401) {
      handleSessionExpired();
    }

    console.error(error);
    throw new ApiError(options, result, error);
  }

  if (!result.ok) {
    const errorStatus = result.status ?? "unknown";
    const errorStatusText = result.statusText ?? "unknown";
    const errorBody = (() => {
      try {
        return JSON.stringify(result.body, null, 2);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return undefined;
      }
    })();
    const message = `Generic Error: status: ${errorStatus}; status text: ${errorStatusText}; body: ${errorBody}`;

    console.error(message);
    throw new ApiError(options, result, message);
  }
};

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @param axiosClient The axios client instance to use
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = <T extends z.ZodSchema, U extends string>(
  config: APIConfigProps,
  options: ApiRequestOptions<U>,
  responseSchema: T,
  axiosClient: AxiosInstance = axios,
) => {
  return new CancelablePromise<z.infer<typeof responseSchema>>(
    async (resolve, reject, onCancel) => {
      try {
        const url = getUrl(config, options);
        const formData = getFormData(options);
        const body = getRequestBody(options);
        const headers = await getHeaders(config, options, formData);

        if (!onCancel.isCancelled) {
          const response = await sendRequest<T, U>(
            config,
            options,
            url,
            body,
            formData,
            headers,
            onCancel,
            axiosClient,
          );
          const responseBody = await getResponseBody(response, responseSchema);
          const responseHeader = getResponseHeader(
            response,
            options.responseHeader,
          );

          const result: ApiResult = {
            body: responseHeader ?? responseBody,
            ok: isSuccess(response.status),
            status: response.status,
            statusText: response.statusText,
            url,
          };

          catchErrorCodes(options, result);

          resolve(result.body);
        }
      } catch (error) {
        reject(error);
      }
    },
  );
};
